/**
 * What the settled-search counter keeps (functions/api/search-stat.ts, D1 search_stat): the words a
 * search settled on, normalised, and never anything that could identify someone — no email address,
 * URL or long number (a phone, card or order number) is ever stored. And, at the end, which searches
 * the browser counts and as what (src/lib/useEventSearch.ts useSearchCount). Pure, unit-tested
 * (scripts/counters.test.mjs). Privacy policy: src/pages/Legal.tsx, "Counting without identifying you".
 */

import { FILLER as SEARCH_FILLER } from './search-helpers.mjs';

/** The words the search ignores (shared/search-helpers.mjs FILLER, so the two cannot drift) — except "vs": "49ers vs rams" stays a pairing in the report. */
const FILLER = new Set([...SEARCH_FILLER].filter((w) => w !== 'vs'));
export const MAX_QUERY = 60;
/** Rows older than this are deleted (the privacy policy says 90 days). */
export const KEEP_DAYS = 90;

/**
 * The search as stored: lowercase words without the filler, single-spaced, at most 60 characters;
 * null when it is under 2 characters or looks like personal data (an @, a URL, 7+ digits in a row
 * once spaces, dots, dashes, slashes, plus signs and brackets are taken out).
 * @param {unknown} raw
 * @returns {string | null}
 */
export const normSearch = (raw) => {
  if (typeof raw !== 'string' || raw.length > 200) return null;
  const s = raw.normalize('NFKC').toLowerCase();
  if (s.includes('@') || s.includes('http') || s.includes('www.')) return null;
  if (/\d{7,}/.test(s.replace(/[\s().\-+/\\]/g, ''))) return null;
  const tokens = s
    .replace(/["*(){}[\]:^~]/g, ' ')
    .split(/[^\p{L}\p{N}']+/u)
    .map((t) => t.replace(/^'+|'+$/g, ''))
    .filter(Boolean)
    .slice(0, 8);
  const kept = tokens.filter((t) => !FILLER.has(t));
  const q = (kept.length ? kept : tokens).join(' ').slice(0, MAX_QUERY).trim();
  return q.length >= 2 ? q : null;
};

/**
 * The top hub a search showed, as its page key: 'kind:slug' (artist:taylor-swift), or a genre page's
 * 'genre:category:slug' (genre:music:jazz-and-blues), else null.
 * @param {unknown} raw
 */
export const cleanHub = (raw) => (typeof raw === 'string' && raw.length <= 80 && /^[a-z]{2,12}:[a-z0-9-]{1,64}(?::[a-z0-9-]{1,64})?$/.test(raw) ? raw : null);

/**
 * How many results it found, 0-100 (null when not a number).
 * @param {unknown} raw
 */
export const clampResults = (raw) => (typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : null);

// ---- the browser's side: which searches count, and as what --------------------------------------

/**
 * A search as the page shows it, in the counter's terms. `answerFor`: the query whose answer from
 * /api/search is on screen — while the next query's is on its way (`pending`), the previous one's;
 * null when nothing is, or when the local scan stands in for a failed or slow API (that is not the
 * search being measured, so it is never counted). `events` and `more`: how many dates and other
 * matches the answer lists; `hubs`: its pages' keys, best first; `notice`: what the API said about
 * it ('none': nothing found); `entity`: the page its reading names ('kind:slug').
 * @typedef {{ answerFor: string | null, pending: boolean, events: number, more: number, hubs: string[], notice?: string | null, entity?: string | null }} SearchView
 */

/**
 * How many results an answer found — its dates, other matches and pages; none when the API says
 * nothing was found (the pages it offers then are near misses under "Nothing on the bill") — and
 * its top page, else null. Two answers are told apart from the rest: the help card (a question
 * for the marketplace, no search: n null, which no zero-result report lists) and an act asked for
 * in a city with no listed date ('elsewhere': n 0 with the act as its top page — unmet demand,
 * though the page shows the act's other dates).
 * @param {SearchView} v
 * @returns {{ n: number | null, hub: string | null }}
 */
export const searchTally = (v) => {
  if (v.notice === 'help') return { n: null, hub: null };
  if (v.notice === 'elsewhere') return { n: 0, hub: v.entity ?? v.hubs[0] ?? null };
  return { n: v.notice === 'none' ? 0 : v.events + v.more + v.hubs.length, hub: v.hubs[0] ?? null };
};

/**
 * The search to count once its answer has stood still: the API's answer for the query asked now
 * (never the previous query's answer still on screen, never the local scan), else null.
 * @param {SearchView} v
 * @returns {{ q: string, n: number | null, hub: string | null } | null}
 */
export const settledSearch = (v) => (v.answerFor !== null && !v.pending ? { q: v.answerFor, ...searchTally(v) } : null);

/**
 * The search a tapped result belongs to: the one whose answer is on screen — the previous query's
 * while the next is on its way — and a tap settles it (the browser counts it then if it had not
 * yet, once like any other); null when the local scan answered.
 * @param {SearchView} v
 * @returns {{ q: string, n: number | null, hub: string | null } | null}
 */
export const tappedSearch = (v) => (v.answerFor !== null ? { q: v.answerFor, ...searchTally(v) } : null);
